import {
  PLATFORM_AUTH_HANDOFF_ACK,
  PLATFORM_AUTH_HANDOFF_READY,
  PLATFORM_AUTH_HANDOFF_REQUEST,
} from "@prismancy/shared";
import { useEffect, useRef } from "react";
import {
  APP_LOGIN_ORIGIN,
  AUTH_TOKEN_KEY,
  SELECTED_WORKSPACE_KEY,
} from "../../constants";
import StorageService from "../../services/StorageService";
import {
  getLegacyHandoffUrl,
  getLegacyMigrationDestination,
} from "./legacyDomainMigrationPolicy";

const createRequestId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const LegacyDomainMigrator = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const requestIdRef = useRef(createRequestId());
  const redirectRef = useRef(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );

  useEffect(() => {
    let completed = false;
    const appOrigin = new URL(APP_LOGIN_ORIGIN).origin;
    const destination = getLegacyMigrationDestination(APP_LOGIN_ORIGIN, window.location);

    const finish = () => {
      if (completed) return;
      completed = true;
      window.location.replace(destination);
    };

    const handleMessage = async (event: MessageEvent<unknown>) => {
      if (
        event.origin !== appOrigin ||
        event.source !== iframeRef.current?.contentWindow ||
        !event.data ||
        typeof event.data !== "object"
      ) return;

      const message = event.data as { type?: string; requestId?: string; success?: boolean };
      if (
        message.type === PLATFORM_AUTH_HANDOFF_READY &&
        message.requestId === requestIdRef.current
      ) {
        const token = await StorageService.getStorageItem<string>(AUTH_TOKEN_KEY);
        const workspaceId = await StorageService.getStorageItem<string>(SELECTED_WORKSPACE_KEY);
        if (!token) {
          const handoff = new URL("/auth/handoff", APP_LOGIN_ORIGIN);
          handoff.searchParams.set("redirect", redirectRef.current);
          window.location.replace(handoff.toString());
          return;
        }
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: PLATFORM_AUTH_HANDOFF_REQUEST,
            requestId: requestIdRef.current,
            token,
            ...(workspaceId ? { workspaceId } : {}),
          },
          appOrigin,
        );
        return;
      }

      if (
        message.type === PLATFORM_AUTH_HANDOFF_ACK &&
        message.requestId === requestIdRef.current &&
        message.success === true
      ) finish();
    };

    window.addEventListener("message", handleMessage);
    const timeout = window.setTimeout(() => {
      if (completed) return;
      const handoff = new URL("/auth/handoff", APP_LOGIN_ORIGIN);
      handoff.searchParams.set("redirect", redirectRef.current);
      window.location.replace(handoff.toString());
    }, 5000);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={getLegacyHandoffUrl(
        APP_LOGIN_ORIGIN,
        requestIdRef.current,
        redirectRef.current,
      )}
      title="Migrate Prismancy session"
      className="hidden"
      aria-hidden="true"
    />
  );
};

export default LegacyDomainMigrator;
